import React, { useEffect, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";

import EditIcon from "assets/images/icons/JSX/Edit"; //Replace with calculator
import classes from "./style.module.scss";

import globalStyles from "components/globalStyles.module.scss";
import Drawer from "components/Drawer";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Button from "components/Button";

import { getDecimalPlaces, getValidation, required } from "utils/fieldValidation";
import { getBaseUnits } from "utils/fields";
import { useStateSelector } from "utils/field";
import { change } from "store/root/actions";
import { getDefaultCompositeObj } from "utils/enclosure/basement";

import { actions } from "features/Model/Enclosure/_ducks";

const { updateCompositeCalc, clearCompositeCalc } = actions;

const areaPercentValidation = [required, ...getValidation("compositeRValueArea")];
const areaPercentValidationSection0 = [required, ...getValidation("compositeRValueAreaSection0")];
const areaPercentRemainingValidation = getValidation("compositeRValueArea");
const basementWallTypeValidation = [required, ...getValidation("basementIntAddedIns")];

const getFieldName = (fieldId) =>
    ({
        basementIntAddedIns: "Interior Added Insulation",
        basementExtAddedIns: "Exterior Added Insulation",
        basementPonyWallType: "Pony Wall",
        crawlspaceWallType: "Crawlspace Wall",
    }[fieldId]);

const drawerContent = ({ name }) => ({
    title: `Composite ${name}`,
    subTitle: `Define the details of your composite ${(name || "").toLowerCase()}`,
    icon: () => <EditIcon fill="#0eb0a9" />,
    introColor: "rgba(24, 193, 173, 0.1)",
});

const CompositeRCalc = React.memo(({ change, reset, formName = "model", updateCompositeCalc, clearCompositeCalc }) => {
    const compositeRCalc = useStateSelector("enclosure.compositeRCalc") || {};
    const {
        open = false,
        invalid,
        fieldId = "",
        fieldAccessor = "",
        isConcrete = true,
        rValUnits = "",
    } = compositeRCalc;

    const currentEffRVal = fieldAccessor.includes("extAddedInsType")
        ? useStateSelector(`${fieldAccessor}`) || 0
        : useStateSelector(`${fieldAccessor}_effRVal`) || 0;

    const currentComposite = fieldAccessor.includes("extAddedInsType")
        ? useStateSelector(`${fieldAccessor.split(".value")[0]}_composite`)
        : useStateSelector(`${fieldAccessor}_composite`);

    const defaultEffRVal = getDefaultCompositeObj(currentEffRVal);

    const [compositeRCalcForm, setCompositeRCalcForm] = useState(defaultEffRVal);

    // Composite form
    const section0Percentage = compositeRCalcForm?.section0?.percentage || 0;
    const section1Percentage = compositeRCalcForm?.section1?.percentage || 0;
    const section2Percentage = compositeRCalcForm?.section2?.percentage || 0;
    const section3Percentage = compositeRCalcForm?.section3?.percentage || 0;
    const section0RSI = compositeRCalcForm?.section0?.rsi || 0;
    const section1RSI = compositeRCalcForm?.section1?.rsi || 0;
    const section2RSI = compositeRCalcForm?.section2?.rsi || 0;
    const section3RSI = compositeRCalcForm?.section3?.rsi || 0;

    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};

    const calculateRSIPortion = (percentage, rsi, coreRVal = 0.116) =>
        percentage !== 0 ? percentage / (rsi + coreRVal) : 0;
    // Calculate total
    const section0 = calculateRSIPortion(section0Percentage, section0RSI, isConcrete ? 0.116 : 0.417);
    const section1 = calculateRSIPortion(section1Percentage, section1RSI, isConcrete ? 0.116 : 0.417);
    const section2 = calculateRSIPortion(section2Percentage, section2RSI, isConcrete ? 0.116 : 0.417);
    const section3 = calculateRSIPortion(section3Percentage, section3RSI, isConcrete ? 0.116 : 0.417);

    const sumQuotient = section0 + section1 + section2 + section3;
    const decimals = getDecimalPlaces(fieldId);

    // Invalid total
    const totalInvalid = false;

    // Invlaid percentages
    const areaPercent = section0Percentage + section1Percentage + section2Percentage;
    const percentInvalid = areaPercent > 100;

    const {
        title,
        subTitle,
        icon: Icon,
        introColor,
    } = drawerContent({
        name: getFieldName(fieldId),
    });

    const disableApply = invalid || totalInvalid || percentInvalid;

    const handleCancel = () => {
        setCompositeRCalcForm(defaultEffRVal);
        clearCompositeCalc();
    };

    const rZeroNotAllowed = ["crawlspaceWallType", "basementPonyWallType"].includes(fieldId);

    const onSubmit = () => {
        let modelCompositeField = `${fieldAccessor}_composite`;
        let modelEffValueField = `${fieldAccessor}_effRVal`;

        if (fieldAccessor.includes("extAddedInsType")) {
            modelEffValueField = `${fieldAccessor}`;
            modelCompositeField = `${fieldAccessor.split(".value")[0]}_composite`;
        }

        change(modelCompositeField, compositeRCalcForm);
        change(modelEffValueField, compositeRCalcForm.effRVal);
        setCompositeRCalcForm(defaultEffRVal);
        clearCompositeCalc();
    };

    // Handle % changes
    useEffect(() => {
        if (compositeRCalcForm) {
            const areaPercent = section0Percentage + section1Percentage + section2Percentage;
            setCompositeRCalcForm({
                ...compositeRCalcForm,
                section3: { ...compositeRCalcForm.section3, percentage: 100 - areaPercent },
            });
        }
    }, [section0Percentage, section1Percentage, section2Percentage]);

    useEffect(() => {
        if (compositeRCalcForm) {
            const totalEffRValue = parseFloat((100 / sumQuotient - (isConcrete ? 0.116 : 0.417)).toFixed(decimals + 2));
            setCompositeRCalcForm({
                ...compositeRCalcForm,
                effRVal: totalEffRValue,
            });
        }
    }, [sumQuotient, isConcrete, decimals]);

    useEffect(() => {
        if (open) {
            const defaultEffRVal = getDefaultCompositeObj(currentEffRVal);
            setCompositeRCalcForm(!isEmpty(currentComposite) ? currentComposite : defaultEffRVal);
        }
    }, [open]);

    return (
        <Drawer open={open} title={title} subtitle={subTitle} close={handleCancel} icon={Icon} introColor={introColor}>
            <InputRow gridTemplate="1fr 1fr" className={classes.header}>
                <h3>Area Fraction</h3>
                <h3>R-Value</h3>
            </InputRow>
            <InputRow gridTemplate="1fr 1fr" className={classes.group}>
                <Input
                    input={{
                        value: section0Percentage,
                        onChange: (value) =>
                            setCompositeRCalcForm({
                                ...compositeRCalcForm,
                                section0: { ...compositeRCalcForm.section0, percentage: value },
                            }),
                    }}
                    type="number"
                    label="Section 1 % of Area"
                    placeholder={0.0}
                    validate={areaPercentValidationSection0}
                    decimals={getDecimalPlaces("compositeRValueArea")}
                />
                <InputWithUnits
                    input={{
                        value: section0RSI,
                        onChange: (value) =>
                            setCompositeRCalcForm({
                                ...compositeRCalcForm,
                                section0: { ...compositeRCalcForm?.section0, rsi: value },
                            }),
                    }}
                    type="number"
                    label={"R-Value"}
                    placeholder={0.0}
                    validate={basementWallTypeValidation}
                    decimals={getDecimalPlaces(fieldId)}
                    units={{
                        base: getBaseUnits(fieldId, modelUnits),
                        options: [rValUnits],
                        selected: rValUnits,
                        unitType: "thermalResistance",
                        accessor: `${fieldAccessor}_u`,
                    }}
                    warning={
                        section0RSI === 0 && section0Percentage !== 0 && rZeroNotAllowed && "compositeSingleZeroRValue"
                    }
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr" className={classes.group}>
                <Input
                    input={{
                        value: section1Percentage,
                        onChange: (value) =>
                            setCompositeRCalcForm({
                                ...compositeRCalcForm,
                                section1: { ...compositeRCalcForm?.section1, percentage: value },
                            }),
                    }}
                    type="number"
                    label="Section 2 % of Area"
                    placeholder={0.0}
                    validate={areaPercentValidation}
                    decimals={getDecimalPlaces("compositeRValueArea")}
                />
                <InputWithUnits
                    input={{
                        value: section1RSI,
                        onChange: (value) =>
                            setCompositeRCalcForm({
                                ...compositeRCalcForm,
                                section1: { ...compositeRCalcForm?.section1, rsi: value },
                            }),
                    }}
                    type="number"
                    label={"R-Value"}
                    placeholder={0.0}
                    validate={basementWallTypeValidation}
                    decimals={getDecimalPlaces(fieldId)}
                    units={{
                        base: getBaseUnits(fieldId, modelUnits),
                        options: [rValUnits],
                        selected: rValUnits,
                        unitType: "thermalResistance",
                        accessor: `${fieldAccessor}_u`,
                    }}
                    warning={
                        section1RSI === 0 && section1Percentage !== 0 && rZeroNotAllowed && "compositeSingleZeroRValue"
                    }
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr" className={classes.group}>
                <Input
                    input={{
                        value: section2Percentage,
                        onChange: (value) =>
                            setCompositeRCalcForm({
                                ...compositeRCalcForm,
                                section2: { ...compositeRCalcForm.section2, percentage: value },
                            }),
                    }}
                    type="number"
                    label="Section 3 % of Area"
                    placeholder={0.0}
                    validate={areaPercentValidation}
                    decimals={getDecimalPlaces("compositeRValueArea")}
                />
                <InputWithUnits
                    input={{
                        value: section2RSI,
                        onChange: (value) =>
                            setCompositeRCalcForm({
                                ...compositeRCalcForm,
                                section2: { ...compositeRCalcForm.section2, rsi: value },
                            }),
                    }}
                    type="number"
                    label={"R-Value"}
                    placeholder={0.0}
                    validate={basementWallTypeValidation}
                    decimals={getDecimalPlaces(fieldId)}
                    units={{
                        base: getBaseUnits(fieldId, modelUnits),
                        options: [rValUnits],
                        selected: rValUnits,
                        unitType: "thermalResistance",
                        accessor: `${fieldAccessor}_u`,
                    }}
                    warning={
                        section2RSI === 0 && section2Percentage !== 0 && rZeroNotAllowed && "compositeSingleZeroRValue"
                    }
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr" className={classes.group}>
                <Input
                    input={{
                        value: section3Percentage,
                        onChange: () => {},
                    }}
                    type="number"
                    label="Remaining % of Area"
                    placeholder={0.0}
                    validate={areaPercentRemainingValidation}
                    decimals={getDecimalPlaces("compositeRValueArea")}
                    disabled
                    warning={percentInvalid ? "compositeAreaFrac" : ""}
                />
                <InputWithUnits
                    input={{
                        value: section3RSI,
                        onChange: (value) =>
                            setCompositeRCalcForm({
                                ...compositeRCalcForm,
                                section3: { ...compositeRCalcForm?.section3, rsi: value },
                            }),
                    }}
                    type="number"
                    label={"R-Value"}
                    placeholder={0.0}
                    validate={basementWallTypeValidation}
                    decimals={getDecimalPlaces(fieldId)}
                    units={{
                        base: getBaseUnits(fieldId, modelUnits),
                        options: [rValUnits],
                        selected: rValUnits,
                        unitType: "thermalResistance",
                        accessor: `${fieldAccessor}_u`,
                    }}
                    warning={
                        section3RSI === 0 && section3Percentage !== 0 && rZeroNotAllowed && "compositeSingleZeroRValue"
                    }
                />
            </InputRow>
            <div className={classes.total}>
                <InputWithUnits
                    input={{
                        value: compositeRCalcForm?.effRVal || 0,
                        onChange: () => {},
                    }}
                    setValue={compositeRCalcForm?.effRVal || 0}
                    type="number"
                    label={"Total Effective R-Value"}
                    placeholder={0.0}
                    validate={basementWallTypeValidation}
                    setTouched={true}
                    decimals={getDecimalPlaces(fieldId)}
                    units={{
                        base: getBaseUnits(fieldId, modelUnits),
                        options: [rValUnits],
                        selected: rValUnits,
                        unitType: "thermalResistance",
                        accessor: `${fieldAccessor}_u`,
                    }}
                    disabled
                    warning={totalInvalid ? "compositeZeroRValue" : ""}
                />
            </div>
            <div className={globalStyles.buttons} style={{ paddingBottom: "3rem" }}>
                <Button large type="hollow" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button
                    large
                    onClick={() => {
                        if (disableApply) {
                            return;
                        }
                        onSubmit();
                    }}
                    disabled={disableApply}
                >
                    {`Apply R-Value`}
                </Button>
            </div>
        </Drawer>
    );
}, isEqual);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    updateCompositeCalc: (updates) => dispatch(updateCompositeCalc(updates)),
    clearCompositeCalc: () => dispatch(clearCompositeCalc()),
    change: (path, value) => dispatch(change(path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompositeRCalc);
